import React from "react";
import { Alert, Button, Card, Dropdown, Popconfirm, Result, Space, Table, Typography } from "antd";
import { DownOutlined, FileExcelOutlined, UndoOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import ExcelExport from "components/shared-components/ExcelExport";
import { formatPrice } from "functions/formatters";
import { getPriceImportOverviewExcelData, getPriceImportRollbackExcelData } from "functions/excel";
import { COMPANY_CONSTANTS } from "constants/CompanyConstants";
import { DATE_FORMAT_DD_MM_YYYY } from "constants/DateConstant";
import { getNotesText, getStatusLabel } from "./labels";

const { Text } = Typography;

const ResultStep = ({
  result,
  appliedLines,
  runId,
  runLogFailed,
  rollbackLoading,
  onRollback,
  onRestart,
  onBackToProducts,
}) => {
  const { t } = useTranslation();

  const applied = result?.applied || [];
  const conflicts = result?.conflicts || [];
  const missing = result?.missing || [];
  const issueCount = conflicts.length + missing.length;
  const failed = !!result?.failed;

  const status = failed ? "error" : issueCount > 0 ? "warning" : "success";
  const title = failed
    ? t("price_import.result.failed_title")
    : issueCount > 0
    ? t("price_import.result.issues_title", { count: applied.length, issues: issueCount })
    : t("price_import.result.success_title", { count: applied.length });
  const description = failed
    ? t("price_import.result.failed_description", { count: applied.length })
    : t("price_import.result.success_description");

  const productColumn = {
    title: t("product"),
    dataIndex: "productName",
    render: (_, line) => (
      <div>
        <div>{line.productName || line.name || "-"}</div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {line.productCode || line.code || "-"}
        </Text>
      </div>
    ),
  };

  const conflictColumns = [
    productColumn,
    {
      title: t("price_import.result.column_expected_price"),
      dataIndex: "oldPrice",
      align: "right",
      render: (value) => <span>€ {formatPrice(value)}</span>,
    },
    {
      title: t("price_import.result.column_current_price"),
      dataIndex: "currentPrice",
      align: "right",
      render: (value) => <span>€ {formatPrice(value)}</span>,
    },
    {
      title: t("price_import.review.column_new_price"),
      dataIndex: "newPrice",
      align: "right",
      render: (value) => <Text type="secondary">€ {formatPrice(value)}</Text>,
    },
  ];

  const missingColumns = [
    productColumn,
    {
      title: t("price_import.review.column_new_price"),
      dataIndex: "newPrice",
      align: "right",
      render: (value) => <span>€ {formatPrice(value)}</span>,
    },
  ];

  const fileDate = dayjs().format(DATE_FORMAT_DD_MM_YYYY);

  // Twee downloads onder één knop, zodat het resultaatscherm niet volloopt met acties.
  const excelMenu = {
    items: [
      {
        key: "result",
        label: (
          <ExcelExport
            data={getPriceImportOverviewExcelData(appliedLines, {
              statusLabel: (line) => getStatusLabel(t, line),
              notesLabel: (line) => getNotesText(t, line),
            })}
            fileName={`${COMPANY_CONSTANTS.WEBSITE_NAME} prijsimport resultaat ${fileDate}`}
          >
            {t("price_import.result.download_result")}
          </ExcelExport>
        ),
      },
      {
        key: "rollback",
        label: (
          <ExcelExport
            data={getPriceImportRollbackExcelData(applied)}
            fileName={`${COMPANY_CONSTANTS.WEBSITE_NAME} oude prijzen ${fileDate}`}
          >
            {t("price_import.result.download_rollback")}
          </ExcelExport>
        ),
      },
    ],
  };

  return (
    <>
      <Card>
        <Result
          status={status}
          title={title}
          subTitle={description}
          extra={
            <Space wrap>
              <Button type="primary" onClick={onBackToProducts}>
                {t("price_import.result.back_to_products")}
              </Button>
              <Button onClick={onRestart}>{t("price_import.result.new_import")}</Button>
              <Dropdown menu={excelMenu}>
                <Button icon={<FileExcelOutlined />}>
                  {t("price_import.result.excel")} <DownOutlined />
                </Button>
              </Dropdown>
              {!!runId && applied.length > 0 && (
                <Popconfirm
                  title={t("price_import.result.rollback_confirm", { count: applied.length })}
                  okText={t("yes")}
                  cancelText={t("no")}
                  onConfirm={onRollback}
                >
                  <Button danger type="text" icon={<UndoOutlined />} loading={rollbackLoading}>
                    {t("price_import.result.rollback")}
                  </Button>
                </Popconfirm>
              )}
            </Space>
          }
        />

        {failed && !!result?.error && <Alert type="error" showIcon message={result.error} />}
        {runLogFailed && (
          <Alert
            style={{ marginTop: 16 }}
            type="warning"
            showIcon
            message={t("price_import.apply.log_failed")}
            description={t("price_import.apply.log_failed_description")}
          />
        )}
      </Card>

      {conflicts.length > 0 && (
        <Card style={{ marginTop: 16 }} title={t("price_import.result.conflicts_title")}>
          <Text type="secondary">{t("price_import.result.conflicts_description")}</Text>
          <Table
            style={{ marginTop: 12 }}
            rowKey={(line) => line.productId}
            size="small"
            columns={conflictColumns}
            dataSource={conflicts}
            pagination={false}
          />
        </Card>
      )}

      {missing.length > 0 && (
        <Card style={{ marginTop: 16 }} title={t("price_import.result.missing_title")}>
          <Text type="secondary">{t("price_import.result.missing_description")}</Text>
          <Table
            style={{ marginTop: 12 }}
            rowKey={(line) => line.productId}
            size="small"
            columns={missingColumns}
            dataSource={missing}
            pagination={false}
          />
        </Card>
      )}
    </>
  );
};

export default ResultStep;
